import * as THREE from 'three';

/**
 * Render budget read-outs for `__bh.renderBudget()` (docs/PERF.md, "Phone performance"). Debug-only:
 * they walk the scene, so call them from tests and the console, not every frame.
 *
 * GPU memory is an estimate from what three.js holds (WebGL has no query for it): the drawing buffer
 * (colour + depth, the browser's display copy, and the multisampled buffers when the canvas has MSAA),
 * the post stack's render targets (post.ts), shadow maps, textures (image size, +1/3 with mipmaps) and
 * vertex / index / instance buffers.
 */
export interface GpuMemory {
  drawingBuffer: number;
  postTargets: number;
  shadowMaps: number;
  textures: number;
  geometry: number;
  total: number;
}

const MB = 1024 * 1024;
const round = (b: number): number => Math.round((b / MB) * 10) / 10;

function textureBytes(t: THREE.Texture): number {
  const img = t.image as { width?: number; height?: number; data?: ArrayBufferView } | null;
  if (!img) return 0;
  // KTX2 model textures (render/ktx2.ts): the transcoded mip chain is what the GPU holds.
  const mips = (t as THREE.CompressedTexture).isCompressedTexture ? (t.mipmaps as { data: ArrayBufferView }[]) : null;
  if (mips?.length) return mips.reduce((s, m) => s + m.data.byteLength, 0);
  if (img.data && 'byteLength' in img.data) return img.data.byteLength * (t.generateMipmaps ? 4 / 3 : 1);
  const w = img.width ?? 0;
  const h = img.height ?? 0;
  const bpp = t.type === THREE.HalfFloatType ? 8 : t.type === THREE.FloatType ? 16 : 4;
  return w * h * bpp * (t.generateMipmaps && t.minFilter !== THREE.LinearFilter && t.minFilter !== THREE.NearestFilter ? 4 / 3 : 1);
}

function collectTextures(value: unknown, out: Set<THREE.Texture>): void {
  if (value instanceof THREE.Texture) out.add(value);
  else if (Array.isArray(value)) for (const v of value) collectTextures(v, out);
}

export function estimateGpuMemory(renderer: THREE.WebGLRenderer, scene: THREE.Scene, postTargetBytes: number, canvasMsaa: boolean): GpuMemory {
  const canvas = renderer.domElement;
  const px = canvas.width * canvas.height;
  // Colour + depth/stencil, plus the composited copy the browser keeps; MSAA adds 4 samples of both.
  const drawingBuffer = px * (4 + 4) + px * 4 + (canvasMsaa ? px * 4 * (4 + 4) : 0);

  const textures = new Set<THREE.Texture>();
  const attrs = new Set<THREE.BufferAttribute | THREE.InterleavedBuffer>();
  let shadowMaps = 0;
  const materials = new Set<THREE.Material>();
  scene.traverse((o) => {
    const light = o as THREE.DirectionalLight;
    if (light.isLight && light.castShadow && light.shadow?.map) {
      const m = light.shadow.map;
      shadowMaps += m.width * m.height * 8;
    }
    const mesh = o as THREE.Mesh;
    if (!mesh.geometry) return;
    const g = mesh.geometry as THREE.BufferGeometry;
    for (const a of Object.values(g.attributes)) attrs.add((a as THREE.InterleavedBufferAttribute).isInterleavedBufferAttribute ? (a as THREE.InterleavedBufferAttribute).data : (a as THREE.BufferAttribute));
    if (g.index) attrs.add(g.index);
    const inst = o as THREE.InstancedMesh;
    if (inst.isInstancedMesh) {
      attrs.add(inst.instanceMatrix);
      if (inst.instanceColor) attrs.add(inst.instanceColor);
    }
    const mat = mesh.material;
    if (Array.isArray(mat)) for (const m of mat) materials.add(m);
    else if (mat) materials.add(mat);
  });
  for (const m of materials) {
    for (const v of Object.values(m)) collectTextures(v, textures);
    // textures a shader patch samples (the street atlas on the city's props / foliage, streetModels.ts)
    collectTextures((m.userData as { textures?: unknown }).textures, textures);
    const u = (m as THREE.ShaderMaterial).uniforms;
    if (u) for (const v of Object.values(u)) collectTextures(v?.value, textures);
  }
  collectTextures(scene.environment, textures);
  collectTextures(scene.background, textures);
  let tex = 0;
  for (const t of textures) tex += textureBytes(t);
  let geo = 0;
  for (const a of attrs) geo += (a.array as ArrayBufferView).byteLength;
  const total = drawingBuffer + postTargetBytes + shadowMaps + tex + geo;
  return { drawingBuffer: round(drawingBuffer), postTargets: round(postTargetBytes), shadowMaps: round(shadowMaps), textures: round(tex), geometry: round(geo), total: round(total) };
}

const frustum = new THREE.Frustum();
const projScreen = new THREE.Matrix4();
const sphere = new THREE.Sphere();

/**
 * Triangles the main pass draws, per top-level scene object (visible meshes in the camera frustum;
 * instanced meshes count every live instance). The shadow pass is extra; `getPerf().triangles` counts all passes.
 */
export function trianglesByGroup(scene: THREE.Scene, camera: THREE.Camera): Record<string, number> {
  camera.updateMatrixWorld();
  projScreen.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
  frustum.setFromProjectionMatrix(projScreen);
  const out: Record<string, number> = {};
  for (const top of scene.children) {
    // The city's chunk meshes are named `material:chunk`: report those per material.
    if (top.children.length > 0 && top.children.every((c) => c.name.includes(':'))) {
      for (const c of top.children) {
        const sub = trianglesOf(c);
        if (sub <= 0) continue;
        const key = `${top.name || top.type}/${c.name.split(':')[0]}`;
        out[key] = (out[key] ?? 0) + Math.round(sub);
      }
      continue;
    }
    const tris = trianglesOf(top);
    if (tris > 0) {
      const key = top.name || top.type;
      out[key] = (out[key] ?? 0) + Math.round(tris);
    }
  }
  return out;
}

/** Main-pass triangles under `root` (against the frustum set by trianglesByGroup). */
function trianglesOf(root: THREE.Object3D): number {
  let tris = 0;
  const walk = (o: THREE.Object3D): void => {
    if (!o.visible) return;
    const mesh = o as THREE.Mesh;
    const inst = o as THREE.InstancedMesh;
    if ((mesh.isMesh || inst.isInstancedMesh) && mesh.geometry) {
      const g = mesh.geometry;
      let inView = true;
      if (o.frustumCulled && !inst.isInstancedMesh) {
        if (!g.boundingSphere) g.computeBoundingSphere();
        sphere.copy(g.boundingSphere!).applyMatrix4(o.matrixWorld);
        inView = frustum.intersectsSphere(sphere);
      }
      if (inView) {
        const per = (g.index ? g.index.count : (g.attributes.position?.count ?? 0)) / 3;
        const count = inst.isInstancedMesh ? inst.count : 1;
        const from = Math.min(per, g.drawRange.start / 3);
        tris += Math.min(per - from, g.drawRange.count === Infinity ? per : g.drawRange.count / 3) * count;
      }
    }
    for (const c of o.children) walk(c);
  };
  walk(root);
  return tris;
}
